// Payment page. The flow is:
//   1. Page loads with the booking summary from /v1/flights/booking/<id>/
//   2. User clicks "Pay" → we POST /reprice/ first
//      a. If price unchanged → cascade straight into POST /book/
//      b. If price changed   → show a confirmation modal with old vs new;
//                              on Accept, run /book/. On Cancel, dismiss.
//   3. /book/ runs with the BookingLoader covering the screen, then we
//      navigate to /confirmation. 402 ("insufficient balance") surfaces
//      inline in the pay-box with `required_inr` / `balance_inr` details.
//
// The standalone Reprice page is gone — it added a navigation hop without
// changing what the user could see.

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiError, api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { fmtCabin, fmtPaxType } from "../lib/labels";
import { useBodyClass } from "../lib/useBodyClass";
import { AirlineLogo } from "../components/AirlineLogo";
import { BookingLoader } from "../components/BookingLoader";
import { FlowStepper } from "./Passengers";
import type { Itinerary } from "../lib/types";

type Phase = "idle" | "repricing" | "awaiting-confirm" | "booking";

export function Payment() {
  useBodyClass("has-bg2");
  const { bookingId } = useParams();
  const nav = useNavigate();
  const { me, refresh } = useAuth();
  const partner = me?.partner;

  const bookingQ = useQuery({
    queryKey: ["booking", bookingId],
    queryFn: () => api.get<Itinerary>(`/v1/flights/booking/${bookingId}/`),
    enabled: !!bookingId,
  });

  const [phase, setPhase] = useState<Phase>("idle");
  // Latest priced itinerary — set after the first reprice. Drives the
  // confirmation modal + the eventual book call.
  const [priced, setPriced] = useState<Itinerary | null>(null);
  // Synthetic sub-step within /book/. The book endpoint is one HTTP call but
  // the user sees three phases (debit → talk to airline → issue ticket). We
  // advance the visible step on a timer so the loader feels like it's tracking
  // real work instead of flashing through to success.
  const [bookSubStep, setBookSubStep] = useState<0 | 1 | 2>(0);
  useEffect(() => {
    if (phase !== "booking") {
      setBookSubStep(0);
      return;
    }
    // Two sub-steps: "Debiting wallet" (0) → "Issuing ticket" (1).
    // We park on step 1 until the API actually completes so the spinner stays
    // alive for the long tail of slow upstream calls instead of "ticking"
    // through a fake third step.
    setBookSubStep(0);
    const t1 = window.setTimeout(() => setBookSubStep(1), 1800);
    return () => { window.clearTimeout(t1); };
  }, [phase]);

  // Reprice mutation
  const reprice = useMutation({
    mutationFn: () => api.post<Itinerary>(`/v1/flights/booking/${bookingId}/reprice/`),
    onSuccess: (data) => {
      setPriced(data);
      const changed = !!data.pricing.price_changed;
      if (changed) {
        setPhase("awaiting-confirm");
      } else {
        // Same price — go straight to book.
        setPhase("booking");
        book.mutate();
      }
    },
    onError: () => setPhase("idle"),
  });

  // Book mutation
  const book = useMutation({
    mutationFn: () => api.post<Itinerary>(`/v1/flights/booking/${bookingId}/book/`),
    onSuccess: async () => {
      await refresh();
      nav(`/booking/${bookingId}/confirmation`);
    },
    onError: () => setPhase("idle"),
  });

  function onPay() {
    if (phase !== "idle") return;
    setPhase("repricing");
    reprice.mutate();
  }
  function onConfirmNewPrice() {
    setPhase("booking");
    book.mutate();
  }
  function onRejectNewPrice() {
    setPhase("idle");
    setPriced(null);
    reprice.reset();
  }

  if (bookingQ.isLoading) return <div className="app-loading"><span className="spinner" />Loading…</div>;
  if (bookingQ.error || !bookingQ.data) return <div className="app-error">Couldn't load booking.</div>;

  // Use the latest priced itinerary if we have one (post-reprice numbers),
  // otherwise the original booking we loaded.
  const b: Itinerary = priced ?? bookingQ.data;
  const item = b.itinerary_items[0];
  const bookError = book.error instanceof ApiError ? book.error : null;
  const repriceError = reprice.error instanceof ApiError ? reprice.error : null;
  const insufficient = bookError?.status === 402;
  const errBody = (bookError?.body || {}) as { required_inr?: number; balance_inr?: number };
  const leadPax = (bookingQ.data as unknown as { passengers?: Array<Record<string, unknown>> }).passengers?.[0];
  const firstSegAirline = item?.legs[0]?.segments[0]?.airline?.name || "the airline";

  // Loader steps reflect the current phase. During the book call we advance
  // an internal sub-step on a timer so each phase visibly completes before the
  // page transitions to confirmation.
  const stateFor = (target: number): "done" | "active" | "pending" =>
    bookSubStep > target ? "done" : bookSubStep === target ? "active" : "pending";
  const loaderSteps: Array<{ label: string; state: "done" | "active" | "pending" }> | null =
    phase === "repricing" ? [
      { label: `Re-confirming fare with ${firstSegAirline}`, state: "active" },
      { label: "Debiting wallet", state: "pending" },
      { label: "Issuing your e-ticket", state: "pending" },
    ]
    : phase === "booking" ? [
      { label: `Fare confirmed with ${firstSegAirline}`, state: "done" },
      { label: `Debiting ${fmtMoney(b.pricing.final_fare, b.pricing.currency)} from wallet`, state: stateFor(0) },
      { label: `Issuing your e-ticket with ${firstSegAirline}`,                                state: stateFor(1) },
    ]
    : null;
  // bookSubStep 2 == all three done; the redirect happens on book.onSuccess so
  // we don't need a separate "done" rendering — but we'll mark the last step
  // as "active" when subStep hits 2 so the spinner stays alive until then.

  const showLoader = phase === "repricing" || phase === "booking";

  return (
    <>
      {showLoader && loaderSteps && (
        <BookingLoader airlineLabel={firstSegAirline} steps={loaderSteps} />
      )}

      <FlowStepper current="payment" />

      <div className="container">
        <div className="pay-wrap">
          <h1 className="pay-h">Final review &amp; <span className="serif">pay</span>.</h1>
          <p className="pay-sub">
            We re-check the price with {firstSegAirline} the moment you tap Pay. If the fare moves you'll see
            the new total before anything is charged. The wallet is debited only after the airline confirms.
          </p>

          <div className="payment-grid">
            {/* LEFT — summary */}
            <div>
              {item?.legs.map((leg, li) => {
                const first = leg.segments[0];
                const last = leg.segments[leg.segments.length - 1];
                const total = leg.segments.reduce((s, x) => s + x.duration_minutes + x.ground_time_minutes, 0);
                return (
                  <div className="itin-block" key={li}>
                    <div className="dir">{capitalize(leg.direction)} · {fmtDateLong(first.origin.departure_time)}</div>
                    <div className="itin-line">
                      <AirlineLogo code={first.airline.code} name={first.airline.name} />
                      <div>
                        <div className="flight-time">{fmtTime(first.origin.departure_time)}</div>
                        <div className="flight-airport">{first.origin.airport_code}{first.origin.terminal ? ` · ${first.origin.terminal}` : ""}</div>
                      </div>
                      <div className="flight-mid">
                        <div className="flight-duration">{fmtDuration(total)}</div>
                        <div className="flight-line" />
                        <div className={`flight-stops${leg.stop_count === 0 ? " direct" : ""}`}>{leg.stop_count === 0 ? "Direct" : `${leg.stop_count} stop`}</div>
                      </div>
                      <div>
                        <div className="flight-time">{fmtTime(last.destination.arrival_time)}</div>
                        <div className="flight-airport">{last.destination.airport_code}{last.destination.terminal ? ` · ${last.destination.terminal}` : ""}</div>
                      </div>
                    </div>
                    <div className="itin-detail">
                      <span><b>{first.airline.code} {first.airline.flight_number}</b> · {fmtCabin(first.cabin_class)}</span>
                      {first.baggage_allowance && <span><b>{first.baggage_allowance}</b>{first.cabin_baggage_allowance ? ` + ${first.cabin_baggage_allowance} cabin` : ""}</span>}
                      <span className={`tag ${item.is_refundable ? "refundable" : "non-refundable"}`}>{item.is_refundable ? "Refundable" : "Non-refundable"}</span>
                    </div>
                  </div>
                );
              })}

              {leadPax && (
                <div className="itin-block">
                  <div className="dir">Passenger</div>
                  <div style={{ fontSize: 15, fontWeight: 600 }}>
                    {[leadPax.title, leadPax.first_name, leadPax.last_name].filter(Boolean).map(String).join(" ")}
                  </div>
                  <div style={{ fontSize: 13, color: "var(--ink-3)", marginTop: 3 }}>
                    {[
                      fmtPaxType(String(leadPax.pax_type ?? "adult")),
                      leadPax.nationality ? String(leadPax.nationality) : null,
                      leadPax.passport_number ? `Passport ${String(leadPax.passport_number)}` : null,
                    ].filter(Boolean).join(" · ")}
                  </div>
                </div>
              )}

              <div className="itin-block">
                <div className="dir">Fare breakdown</div>
                <div className="fare-line"><span className="lbl">Base fare</span><span>{fmtMoney(b.pricing.base_fare, b.pricing.currency)}</span></div>
                {b.pricing.tax_and_surcharge != null && (
                  <div className="fare-line"><span className="lbl">Taxes &amp; surcharge</span><span>{fmtMoney(b.pricing.tax_and_surcharge, b.pricing.currency)}</span></div>
                )}
                <div className="fare-line"><span className="lbl">Service fee</span><span>{fmtMoney(b.pricing.service_fee ?? 0, b.pricing.currency)}</span></div>
                <div className="fare-line total"><span className="lbl">You'll pay</span><span>{fmtMoney(b.pricing.final_fare, b.pricing.currency)}</span></div>
              </div>
            </div>

            {/* RIGHT — pay box */}
            <aside>
              <div className="pay-box">
                <div className="pay-amount-block">
                  <div className="lbl">You'll pay</div>
                  <div className="amount">{fmtMoney(b.pricing.final_fare, b.pricing.currency)}</div>
                  <div className="amount-sub">all inclusive · debited from wallet in INR</div>
                </div>

                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-3)", marginBottom: 10 }}>Payment method</div>
                <div className="pay-method razorpay">
                  <div className="ic ic-rzp" style={{ background: "var(--ink-rail)", color: "var(--yellow)" }}>W</div>
                  <div className="info">
                    <b>Partner wallet</b>
                    <span>
                      Balance: {partner ? <b style={{ color: "var(--ink)" }}>{fmtMoney(partner.balance, "INR")}</b> : "—"}
                      {partner && ` · ${partner.name}`}
                    </span>
                  </div>
                  <div className="check">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18 }}><path d="M5 12l5 5L20 7" /></svg>
                  </div>
                </div>

                {/* Error surfaces — backend message rendered as-is */}
                {repriceError && (
                  <div className="info-banner warn" style={{ margin: "14px 0 0" }}>
                    <span><b>Couldn't re-check fare.</b> {repriceError.message}</span>
                  </div>
                )}
                {insufficient && (
                  <div className="info-banner warn" style={{ margin: "14px 0 0" }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" /></svg>
                    <span>
                      <b>Insufficient wallet balance.</b>{" "}
                      Need {fmtMoney(errBody.required_inr ?? 0, "INR")} · have {fmtMoney(errBody.balance_inr ?? 0, "INR")}.
                      Top up via Django admin and retry.
                    </span>
                  </div>
                )}
                {bookError && !insufficient && (
                  <div className="info-banner warn" style={{ margin: "14px 0 0" }}>
                    <span>{bookError.message}</span>
                  </div>
                )}

                <button
                  type="button"
                  className={`pay-btn${showLoader ? " loading" : ""}`}
                  disabled={showLoader}
                  onClick={onPay}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 9V7a5 5 0 0 1 10 0v2M3 9h14a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2Z" transform="translate(2,0)" /></svg>
                  Pay {fmtMoney(b.pricing.final_fare, b.pricing.currency)} from wallet
                </button>

                <div className="pay-securetext">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
                  Wallet ledgered with every debit · automatic refund on airline reject
                </div>

                <div className="note-block">
                  <b>What happens next.</b><br />
                  We re-check the fare with {firstSegAirline}, then debit your INR wallet, then issue your ticket — usually under 30 seconds end-to-end. If anything fails after the debit we
                  <span className="serif"> automatically refund</span> back to your wallet.
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>

      {phase === "awaiting-confirm" && priced && (
        <PriceChangedModal
          previous={priced.pricing.previous_total_amount ?? null}
          next={priced.pricing.final_fare}
          currency={priced.pricing.currency}
          onAccept={onConfirmNewPrice}
          onCancel={onRejectNewPrice}
        />
      )}
    </>
  );
}

// ─── Price-changed confirmation modal ──────────────────────────────────────

function PriceChangedModal(props: {
  previous: number | null;
  next: number;
  currency: string;
  onAccept: () => void;
  onCancel: () => void;
}) {
  const diff = props.previous != null ? props.next - props.previous : 0;
  return (
    <div className="drawer-overlay" onClick={props.onCancel} style={{ display: "grid", placeItems: "center" }}>
      <div
        role="dialog"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(480px, 92vw)", background: "var(--bg)",
          borderRadius: 18, padding: 26,
          boxShadow: "0 24px 60px rgba(11,18,32,0.25)",
        }}
      >
        <div className="info-banner warn" style={{ margin: "0 0 14px" }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" /></svg>
          <span><b>Fare moved {diff > 0 ? "up" : "down"} by {fmtMoney(Math.abs(diff), props.currency)}.</b> Confirm before we debit your wallet.</span>
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 14, marginBottom: 18 }}>
          {props.previous != null && (
            <span style={{ fontSize: 18, color: "var(--ink-4)", textDecoration: "line-through", fontWeight: 600 }}>
              {fmtMoney(props.previous, props.currency)}
            </span>
          )}
          <span style={{ fontSize: 30, fontWeight: 800, letterSpacing: "-0.02em" }}>
            {fmtMoney(props.next, props.currency)}
          </span>
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button type="button" className="btn btn-ghost btn-pill" onClick={props.onCancel}>
            Don't pay yet
          </button>
          <button type="button" className="btn btn-yellow btn-pill" onClick={props.onAccept}>
            Accept &amp; pay
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── helpers ────────────────────────────────────────────────────────────────

function fmtTime(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false });
}
function fmtDateLong(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
}
function fmtDuration(mins: number): string {
  if (!Number.isFinite(mins)) return "—";
  const h = Math.floor(mins / 60); const m = mins % 60;
  return h === 0 ? `${m}m` : m === 0 ? `${h}h` : `${h}h ${String(m).padStart(2, "0")}m`;
}
function fmtMoney(n: number, currency: string): string {
  try { return new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 0 }).format(n); }
  catch { return `${currency} ${Math.round(n).toLocaleString("en-IN")}`; }
}
function capitalize(s: string): string { return s ? s[0].toUpperCase() + s.slice(1) : s; }
